// Liste der Eingänge (Handoff §2.3) inkl. aller Zustände: Laden, Fehler, leer, keine Treffer.
import { CircleNotch, FileText, LinkSimple, Users, WarningCircle } from '@phosphor-icons/react';
import { STATUS_LABEL } from '../../../../lib/selbstauskunft/status';
import { STATUS_FARBE, initialen } from '../../../../lib/selbstauskunft/darstellung';
import { formatDatum, objektZeile, personenzahl, wert, type Eingang } from './typen';

export function Avatar({ name, farbe, groesse }: { name: string; farbe: string; groesse: 42 | 60 }) {
  return (
    <span aria-hidden className="inline-flex shrink-0 items-center justify-center"
      style={{ width: groesse, height: groesse, borderRadius: '50%', fontSize: groesse === 42 ? 14 : 20, fontWeight: 800,
        background: `color-mix(in srgb, ${farbe} 12%, transparent)`, color: farbe, boxShadow: `inset 0 0 0 2px ${farbe}` }}>
      {initialen(name)}
    </span>
  );
}

function Zeile({ e, aktiv, onWahl }: { e: Eingang; aktiv: boolean; onWahl: () => void }) {
  const farbe = STATUS_FARBE[e.status];
  return (
    <button type="button" onClick={onWahl} aria-current={aktiv || undefined}
      className={`flex w-full items-center text-left ${aktiv ? '' : 'immo-zeile-hover'}`}
      style={{ gap: 12, padding: '11px 12px', borderRadius: 18, border: 'none', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--ink)',
        opacity: e.status === 'abgelehnt' && !aktiv ? 0.6 : 1,
        background: aktiv ? 'var(--acc-soft)' : 'transparent', boxShadow: aktiv ? 'inset 3px 0 0 var(--acc)' : 'none' }}>
      <Avatar name={e.name} farbe={farbe} groesse={42} />
      <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
        <span className="flex items-baseline justify-between" style={{ gap: 8 }}>
          <span className="min-w-0 truncate" style={{ fontSize: 15, fontWeight: 800 }}>{e.name}</span>
          {/* Netto ist Freitext und kann lang sein — gekürzt, voller Text im Tooltip */}
          <span className="truncate tabular-nums" data-tip={(e.daten?.netto_einkommen || '').length > 14 ? `Haushalts-Netto – ${e.daten?.netto_einkommen}` : undefined}
            style={{ fontSize: 14, fontWeight: 800, maxWidth: '48%', flexShrink: 0 }}>{wert(e.daten?.netto_einkommen)}</span>
        </span>
        <span className="truncate" style={{ fontSize: 12, color: 'var(--ink2)' }}>{objektZeile(e)} · {personenzahl(e)} Pers.</span>
        <span className="flex items-center" style={{ gap: 6, fontSize: 11.5, fontWeight: 700, color: farbe }}>
          <span aria-hidden style={{ width: 6, height: 6, borderRadius: '50%', background: farbe }} />
          {STATUS_LABEL[e.status]} · {formatDatum(e.created_at)}
        </span>
      </span>
    </button>
  );
}

export function EingangsListe({ laedt, fehler, gesamt, gefiltert, filterAktiv, ausgewaehltId, onWahl, onNeuLaden, onLinks }: {
  laedt: boolean; fehler: string | null; gesamt: number; gefiltert: Eingang[]; filterAktiv: boolean;
  ausgewaehltId: string | null; onWahl: (id: string) => void; onNeuLaden: () => void; onLinks: () => void;
}) {
  const mitte = 'flex flex-1 flex-col items-center justify-center text-center';
  let inhalt;
  if (laedt) {
    inhalt = (
      <div className={mitte} style={{ gap: 14, padding: '80px 20px' }}>
        <CircleNotch size={30} weight="bold" color="var(--acc)" className="animate-spin" />
        <span style={{ fontSize: 13, color: 'var(--ink2)' }}>Lade Eingänge…</span>
      </div>
    );
  } else if (fehler) {
    inhalt = (
      <div className={mitte} role="alert" style={{ gap: 10, padding: '60px 24px' }}>
        <span className="inline-flex items-center justify-center" style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--bad-soft)' }}>
          <WarningCircle size={26} weight="fill" color="var(--bad)" />
        </span>
        <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--bad)' }}>Eingänge konnten nicht geladen werden</span>
        <span className="break-words" style={{ fontSize: 12, color: 'var(--ink2)' }}>{fehler}</span>
        <button type="button" onClick={onNeuLaden}
          style={{ marginTop: 6, height: 38, padding: '0 16px', borderRadius: 19, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink)' }}>
          Erneut versuchen
        </button>
      </div>
    );
  } else if (gesamt === 0) {
    inhalt = (
      <div className={mitte} style={{ gap: 10, padding: '60px 24px' }}>
        <span className="inline-flex items-center justify-center" style={{ width: 56, height: 56, borderRadius: 18, background: 'var(--acc-soft)' }}>
          <FileText size={28} weight="fill" color="var(--acc)" />
        </span>
        <span style={{ fontSize: 15, fontWeight: 800 }}>Noch keine Selbstauskünfte eingegangen</span>
        <span style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink2)', maxWidth: 280 }}>
          Lege einen öffentlichen Formular-Link an, damit Interessenten ihre Selbstauskunft online ausfüllen können.
        </span>
        <button type="button" onClick={onLinks} className="inline-flex items-center"
          style={{ marginTop: 6, height: 40, padding: '0 18px', gap: 7, borderRadius: 20, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 800,
            background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }}>
          <LinkSimple size={14} weight="bold" /> Links verwalten
        </button>
      </div>
    );
  } else if (gefiltert.length === 0) {
    inhalt = (
      <div className={mitte} style={{ gap: 10, padding: '70px 24px' }}>
        <Users size={32} color="var(--ink3)" />
        <span style={{ fontSize: 13, color: 'var(--ink2)' }}>{filterAktiv ? 'Keine Eingänge in dieser Auswahl' : 'Keine Eingänge in diesem Status'}</span>
      </div>
    );
  } else {
    inhalt = gefiltert.map((e) => <Zeile key={e.id} e={e} aktiv={e.id === ausgewaehltId} onWahl={() => onWahl(e.id)} />);
  }
  return (
    <section aria-label="Eingänge" className="immo-glas flex min-w-0 flex-col" style={{ borderRadius: 26, padding: 8, gap: 2, minHeight: 420 }}>
      {inhalt}
      {!laedt && !fehler && gesamt > 0 && (
        <span style={{ marginTop: 'auto', padding: '10px 12px 6px', fontSize: 11.5, color: 'var(--ink3)' }}>{gefiltert.length} von {gesamt} Eingängen im Filter</span>
      )}
    </section>
  );
}
